import { useParams } from '@tanstack/react-router';
import {
  Ban,
  CheckCircle2,
  ClipboardCheck,
  HandCoins,
  PackageCheck,
  Truck,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ReturnDetail as ReturnDetailData } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { OrderLink, useOrder } from '@/features/orders';
import { useStoreDetail } from '@/features/stores';
import { useStore } from '@/shared/store/store-context';

import { ConfirmMoveDialog } from '../components/confirm-move-dialog';
import { InspectDialog } from '../components/inspect-dialog';
import { ResolveDialog } from '../components/resolve-dialog';
import { ReturnDetailsCard } from '../components/return-details-card';
import { ReturnLinesCard } from '../components/return-lines-card';
import { ReturnResolutionLabel, ReturnStatusBadge } from '../components/return-status-badge';
import { useReturn } from '../hooks/use-return';
import { useUpdateReturn } from '../hooks/use-return-mutations';
import { allowedFromConflict, canResolve, nextStatuses } from '../schemas/return-rules';

const ROUTE = '/_authed/_active/s/$storeId/returns/$returnId' as const;

type Dialog = 'inspect' | 'resolve' | null;

function ReturnDetail({ ret }: { ret: ReturnDetailData }) {
  const { t } = useTranslation('returns');
  const format = useFormat();
  const store = useStore();
  const { can } = usePermissions();
  const message = useErrorMessage();
  // Prefetched by the route loader, so these resolve from cache.
  const { data: order } = useOrder(store.id, ret.orderId);
  const { data: storeDetail } = useStoreDetail(store.id);
  const step = useUpdateReturn(store.id, 'returns:toasts.moved');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [confirm, setConfirm] = useState<'rejected' | 'cancelled' | null>(null);

  const moves = nextStatuses(ret.status);
  const writable = can('returns.write');
  const move = (status: 'approved' | 'in_transit' | 'received') =>
    step.mutate(
      { id: ret.id, body: { status } },
      {
        onError: (error) => {
          const allowed = allowedFromConflict(error);
          toast.error(
            allowed
              ? t('errors.moveRefused', {
                  allowed: allowed.map((m) => t(`status.${m}`)).join(', ') || '—',
                })
              : message(error),
          );
        },
      },
    );
  const busy = (status: string) => step.isPending && step.variables.body.status === status;

  return (
    <PageContent>
      <PageHeader
        tour="returns.detail.header"
        title={<span className="font-mono">{ret.rmaNumber}</span>}
        meta={
          <>
            <ReturnStatusBadge status={ret.status} />
            {ret.resolution ? (
              <span className="text-muted-foreground text-sm">
                <ReturnResolutionLabel resolution={ret.resolution} />
              </span>
            ) : null}
          </>
        }
        description={
          <span className="flex flex-wrap items-center gap-1">
            <span title={format.utc(ret.requestedAt)}>
              {t('detail.requested', { when: format.relative(ret.requestedAt) })}
            </span>
            <span aria-hidden>·</span>
            <span>{t('detail.order')}</span>
            <OrderLink storeId={store.id} orderId={ret.orderId} />
            <span aria-hidden>·</span>
            <span>
              {t('detail.paidBy', {
                who: ret.returnShippingPaidBy
                  ? t(`paidBy.${ret.returnShippingPaidBy}`)
                  : t('paidBy.unset'),
              })}
            </span>
          </span>
        }
        backTo={{ to: '/s/$storeId/returns', params: { storeId: store.id } }}
        backLabel={t('title')}
        actions={
          writable ? (
            <>
              {moves.length > 0 ? (
                <div
                  className="flex flex-wrap items-center gap-2"
                  data-tour="returns.detail.actions"
                >
                  {moves.includes('cancelled') ? (
                    <Button variant="outline" onClick={() => setConfirm('cancelled')}>
                      <Ban aria-hidden />
                      {t('actions.cancel')}
                    </Button>
                  ) : null}
                  {moves.includes('rejected') ? (
                    <Button variant="outline" onClick={() => setConfirm('rejected')}>
                      <XCircle aria-hidden />
                      {t('actions.reject')}
                    </Button>
                  ) : null}
                  {moves.includes('in_transit') ? (
                    <Button
                      variant="outline"
                      disabled={busy('in_transit')}
                      onClick={() => move('in_transit')}
                    >
                      <Truck aria-hidden />
                      {t('actions.inTransit')}
                    </Button>
                  ) : null}
                  {moves.includes('approved') ? (
                    <Button disabled={busy('approved')} onClick={() => move('approved')}>
                      <CheckCircle2 aria-hidden />
                      {t('actions.approve')}
                    </Button>
                  ) : null}
                  {moves.includes('received') ? (
                    <Button disabled={busy('received')} onClick={() => move('received')}>
                      <PackageCheck aria-hidden />
                      {t('actions.receive')}
                    </Button>
                  ) : null}
                  {moves.includes('inspected') ? (
                    <Button onClick={() => setDialog('inspect')}>
                      <ClipboardCheck aria-hidden />
                      {t('actions.inspect')}
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {canResolve(ret.status) && order ? (
                <Button onClick={() => setDialog('resolve')} data-tour="returns.detail.resolve">
                  <HandCoins aria-hidden />
                  {t('actions.resolve')}
                </Button>
              ) : null}
            </>
          ) : null
        }
      />
      <FormLayout
        aside={
          <ReturnDetailsCard
            key={ret.updatedAt}
            storeId={store.id}
            ret={ret}
            readOnly={!writable}
          />
        }
      >
        <ReturnLinesCard ret={ret} order={order} />
        <Can permission="returns.write">
          <p className="text-muted-foreground text-sm" data-tour="returns.detail.hint">
            {t(`hint.${ret.status}`)}
          </p>
        </Can>
      </FormLayout>
      {dialog === 'inspect' ? (
        <InspectDialog
          storeId={store.id}
          ret={ret}
          order={order}
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'resolve' && order ? (
        <ResolveDialog
          storeId={store.id}
          ret={ret}
          order={order}
          feePercent={storeDetail?.settings.returns.restockingFeePercent ?? 0}
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
        />
      ) : null}
      <ConfirmMoveDialog
        storeId={store.id}
        ret={ret}
        move={confirm}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
      />
    </PageContent>
  );
}

function ReturnDetailLoader() {
  const store = useStore();
  const { returnId } = useParams({ from: ROUTE });
  const { data: ret } = useReturn(store.id, returnId);
  return ret ? <ReturnDetail ret={ret} /> : null;
}

export function ReturnDetailPage() {
  return (
    <FeatureErrorBoundary name="returns.detail">
      <ReturnDetailLoader />
    </FeatureErrorBoundary>
  );
}
